import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { api, toast } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, ClipboardList, BarChart3, Clock, AlertTriangle, CheckCircle2, Edit, Download, Send, History } from "lucide-react";
import CheckingQueueCard from "../components/CheckingQueueCard";
import { useNavigate } from "react-router-dom";
import { fh } from "@/utils/hours";
import { LogCallDialog } from "../components/calls/LogCallDialog";

const PERIOD_OPTIONS = [
  { value: "this_month", label: "This Month" },
  { value: "this_quarter", label: "This Quarter" },
  { value: "this_year", label: "This Year" },
];

const COMPLETED_PERIOD_OPTIONS = [
  { value: "this_month", label: "This Month" },
  { value: "this_quarter", label: "This Quarter" },
  { value: "last_90_days", label: "Last 90 Days" },
  { value: "all_time", label: "All Time" },
];

function FollowUpsCard({ user }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calling, setCalling] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/follow-ups?status=pending");
      setRows(res.data || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const today = localISO(new Date());
  const week = localISO(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
  const overdue = rows.filter(r => r.due_date < today);
  const dueToday = rows.filter(r => r.due_date === today);
  const soon = rows.filter(r => r.due_date > today && r.due_date <= week);
  const later = rows.filter(r => r.due_date > week);

  const markDone = async (id) => {
    try {
      await api.patch(`/follow-ups/${id}`, { status: "done" });
      toast.success("Follow-up marked done");
      load();
    } catch {
      toast.error("Failed to mark done");
    }
  };

  const renderRow = (r, isOverdue = false) => (
    <div key={r.id} className="flex items-center justify-between gap-2 py-2 border-b last:border-b-0 text-sm">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono font-semibold text-md3-primary">{r.due_date}</span>
          <span className="text-md3-on-surface-variant">·</span>
          <span className="font-medium">{r.source_type === "call" ? `Call back · ${r.entity_name || (r.entity_type === "lead" ? "Lead" : "Client")}` : r.source_type === "estimation" ? `Estimation ${r.estimation_number || ""}` : (r.invoice_number || "Invoice")}</span>
          {r.source_type === "call" && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{r.entity_type === "lead" ? "Lead" : "Client"}</Badge>}
          {r.source_type === "estimation" && r.project_name && <span className="text-md3-on-surface-variant text-xs">({r.project_name})</span>}
          {r.project_number && <span className="text-md3-on-surface-variant text-xs">({r.project_number} — {r.project_name})</span>}
          {isOverdue && <Badge className="bg-red-100 text-red-700 text-xs px-1.5 py-0">Overdue</Badge>}
        </div>
        {r.notes && <p className="text-xs text-md3-on-surface-variant mt-0.5 truncate">{r.notes}</p>}
      </div>
      <div className="flex gap-1 flex-shrink-0">
        {r.source_type === "call" ? (
          <Button size="sm" variant="outline" className="h-8 rounded-md3-full" onClick={() => setCalling(r)} data-testid={`fu-log-call-${r.id}`}>Log Call</Button>
        ) : (
          <Button size="sm" variant="outline" className="h-8 rounded-md3-full" onClick={() => navigate(r.source_type === "estimation" ? `/sales?notes=${r.source_id}` : `/invoices/${r.source_id}`)} data-testid={`fu-open-${r.id}`}>Open</Button>
        )}
        <Button size="sm" className="h-8 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary" onClick={() => markDone(r.id)} data-testid={`fu-done-${r.id}`}>Done</Button>
      </div>
    </div>
  );

  return (
    <Card className="shadow-md3-1 rounded-md3-lg border-0 bg-md3-surface-container" data-testid="follow-ups-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-md3-title-large flex items-center gap-2">
          <Clock className="h-5 w-5 text-md3-primary" />
          My Follow-Ups {rows.length > 0 && <Badge variant="outline">{rows.length}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-md3-on-surface-variant">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-md3-on-surface-variant">No pending follow-ups. Invoice reminders and calls marked &ldquo;Follow-up Needed&rdquo; appear here on their date.</p>
        ) : (
          <div className="space-y-3">
            {overdue.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-red-600 font-semibold mb-1">Overdue</p>
                {overdue.map(r => renderRow(r, true))}
              </div>
            )}
            {dueToday.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-md3-on-surface-variant font-semibold mb-1">Due Today</p>
                {dueToday.map(r => renderRow(r))}
              </div>
            )}
            {soon.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-md3-on-surface-variant font-semibold mb-1">This Week</p>
                {soon.map(r => renderRow(r))}
              </div>
            )}
            {later.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-md3-on-surface-variant font-semibold mb-1">Later</p>
                {later.map(r => renderRow(r))}
              </div>
            )}
          </div>
        )}
        {calling && (
          <LogCallDialog open onClose={() => setCalling(null)} preset={{ entity_type: calling.entity_type, entity_id: calling.source_id, name: calling.entity_name }} onLogged={() => load()} />
        )}
      </CardContent>
    </Card>
  );
}

export default function MyWorkspace({ user }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("active");
  const [activeTasks, setActiveTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [completedPeriod, setCompletedPeriod] = useState("this_month");
  const [tasksLoading, setTasksLoading] = useState(true);

  const [performance, setPerformance] = useState(null);
  const [perfPeriod, setPerfPeriod] = useState("this_month");
  const [perfLoading, setPerfLoading] = useState(true);

  // Update task dialog
  const [editTask, setEditTask] = useState(null);
  const [taskLogs, setTaskLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logForm, setLogForm] = useState({ date: "", hours: "", status: "partial", percentage: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [pullLoading, setPullLoading] = useState(false);

  const handlePullWorkLogs = async () => {
    setPullLoading(true);
    try {
      const response = await api.post("/timesheets/pull-from-work-logs", {});
      const { message, pulled_count, already_pulled_count, conflicts, start_date, end_date, grace_active, grace_reason } = response.data;
      const rangeLabel = start_date && end_date ? ` (${start_date} → ${end_date})` : "";
      const graceSuffix = grace_active && grace_reason ? ` ${grace_reason}` : "";
      if (pulled_count > 0) {
        toast.success(`${message}${rangeLabel}${graceSuffix}`);
      } else if (already_pulled_count > 0 && (!conflicts || conflicts.length === 0)) {
        toast.info(`${message}${rangeLabel}${graceSuffix}`);
      } else if (!conflicts || conflicts.length === 0) {
        toast.info(`${message || "No work logs found in this period"}${rangeLabel}${graceSuffix}`);
      }
      if (conflicts && conflicts.length > 0) {
        toast.warning(`${conflicts.length} conflict(s) need your decision. Opening Timesheets to resolve...`);
        setTimeout(() => navigate("/timesheets"), 800);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to pull work logs");
    } finally {
      setPullLoading(false);
    }
  };

  const fetchActive = useCallback(async () => {
    try {
      const res = await api.get(`/dashboard/my-tasks?status=active`);
      setActiveTasks(res.data?.tasks || []);
    } catch {
      toast.error("Failed to load active tasks");
    }
  }, []);

  const fetchCompleted = useCallback(async (period) => {
    try {
      const res = await api.get(`/dashboard/my-tasks?status=completed&period=${period}`);
      setCompletedTasks(res.data?.tasks || []);
    } catch {
      toast.error("Failed to load completed tasks");
    }
  }, []);

  const fetchPerformance = useCallback(async (period) => {
    try {
      setPerfLoading(true);
      const res = await api.get(`/dashboard/my-performance?period=${period}`);
      setPerformance(res.data);
    } catch {
      toast.error("Failed to load performance");
    } finally {
      setPerfLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setTasksLoading(true);
      await Promise.all([fetchActive(), fetchCompleted(completedPeriod)]);
      setTasksLoading(false);
    })();
  }, [fetchActive, fetchCompleted, completedPeriod]);

  useEffect(() => {
    fetchPerformance(perfPeriod);
  }, [perfPeriod, fetchPerformance]);

  const openEdit = async (t) => {
    setEditTask(t);
    setLogForm({
      date: new Date().toISOString().slice(0, 10),
      hours: "",
      status: "partial",
      percentage: "",
      note: ""
    });
    setLogsLoading(true);
    try {
      const res = await api.get(`/planning/tasks/${t.id}/work-logs`);
      setTaskLogs(res.data?.logs || []);
    } catch {
      setTaskLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleAddLog = async () => {
    if (!editTask) return;
    if (!logForm.date) { toast.error("Date is required"); return; }
    const hrs = parseFloat(logForm.hours);
    if (Number.isNaN(hrs) || hrs <= 0) { toast.error("Hours must be > 0"); return; }
    if (logForm.status === "partial" && (!logForm.percentage || +logForm.percentage < 1 || +logForm.percentage > 99)) {
      toast.error("Partial requires percentage 1-99"); return;
    }
    setSaving(true);
    try {
      const payload = {
        date: logForm.date,
        hours: hrs,
        status: logForm.status,
        note: logForm.note.trim(),
      };
      if (logForm.status === "partial") payload.percentage = parseInt(logForm.percentage, 10);
      const res = await api.post(`/planning/tasks/${editTask.id}/work-logs`, payload);
      if (res.data?.over_allocation) {
        toast.warning(`Hours exceed allocated (${fh(editTask.allocated_hours)}h)`);
      } else {
        toast.success("Work log added");
      }
      // Refresh logs + tasks
      const logsRes = await api.get(`/planning/tasks/${editTask.id}/work-logs`);
      setTaskLogs(logsRes.data?.logs || []);
      setLogForm({ ...logForm, hours: "", percentage: "", note: "" });
      await Promise.all([fetchActive(), fetchCompleted(completedPeriod)]);
      // Close modal if task is done
      if (res.data?.task?.status === "done") setEditTask(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add log");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLog = async (logId) => {
    if (!confirm("Delete this work log? Task hours and progress will be recomputed.")) return;
    try {
      await api.delete(`/planning/tasks/${editTask.id}/work-logs/${logId}`);
      toast.success("Log deleted");
      const logsRes = await api.get(`/planning/tasks/${editTask.id}/work-logs`);
      setTaskLogs(logsRes.data?.logs || []);
      await Promise.all([fetchActive(), fetchCompleted(completedPeriod)]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const statusBadge = (s) => {
    const map = {
      todo: { className: "bg-gray-200 text-gray-700", label: "Todo" },
      in_progress: { className: "bg-blue-100 text-blue-700", label: "In Progress" },
      done: { className: "bg-green-100 text-green-700", label: "Done" },
    };
    const cfg = map[s] || map.todo;
    return <Badge className={cfg.className}>{cfg.label}</Badge>;
  };

  const workflowBadge = (t) => {
    const wf = t.workflow_state;
    if (!wf || wf === "in_progress") {
      if (t.role === "producer" && (t.current_cycle || 1) > 1) {
        return <Badge className="bg-orange-100 text-orange-700 border border-orange-200">Rework Cycle {t.current_cycle}</Badge>;
      }
      return null;
    }
    if (wf === "ready_for_check") return <Badge className="bg-amber-100 text-amber-700 border border-amber-200">Awaiting Check</Badge>;
    if (wf === "checking") return <Badge className="bg-blue-100 text-blue-700 border border-blue-200">In Review</Badge>;
    if (wf === "rework") return <Badge className="bg-red-100 text-red-700 border border-red-200">Rework — Cycle {t.current_cycle}</Badge>;
    if (wf === "done") return <Badge className="bg-green-100 text-green-700 border border-green-200">Approved</Badge>;
    return null;
  };

  const handleSendForCheck = async (taskId) => {
    if (!confirm("Send this task to the checker? You won't be able to log more hours unless it comes back for rework.")) return;
    try {
      await api.post(`/planning/tasks/${taskId}/send-for-check`);
      toast.success("Task sent for checking");
      await Promise.all([fetchActive(), fetchCompleted(completedPeriod)]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send for checking");
    }
  };

  const [historyTask, setHistoryTask] = useState(null);

  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === "done") return false;
    const today = new Date(); today.setHours(0,0,0,0);
    const due = new Date(dueDate + "T00:00:00");
    return due < today;
  };

  const renderTaskTable = (tasks, showCompletedDate = false) => {
    if (tasks.length === 0) {
      return (
        <div className="text-center py-12 text-md3-on-surface-variant text-sm">
          {showCompletedDate ? "No completed tasks in this period." : "No active tasks assigned to you. 🎉"}
        </div>
      );
    }
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-md3-surface-container border-b border-md3-outline-variant">
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold">Project</th>
              <th className="px-3 py-2 font-semibold">Activity</th>
              <th className="px-3 py-2 font-semibold">Task</th>
              <th className="px-3 py-2 font-semibold text-right">Hours</th>
              <th className="px-3 py-2 font-semibold text-right">Progress</th>
              <th className="px-3 py-2 font-semibold">{showCompletedDate ? "Completed" : "Due"}</th>
              <th className="px-3 py-2 font-semibold">Last Logged</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.id} data-testid={`my-task-row-${t.id}`} className="border-b border-md3-outline-variant hover:bg-md3-surface-container-low">
                <td className="px-3 py-2">
                  <div className="font-mono text-xs text-md3-primary">{t.project_number}</div>
                  <div className="text-xs text-md3-on-surface-variant truncate max-w-[160px]" title={t.project_name}>{t.project_name}</div>
                </td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className="capitalize text-xs">{t.activity_type?.replace(/_/g, " ")}</Badge>
                </td>
                <td className="px-3 py-2 max-w-xs truncate" title={t.description}>{t.description}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <span className="font-medium">{fh(t.actual_hours)}</span>
                  <span className="text-md3-on-surface-variant"> / {fh(t.allocated_hours)}</span>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-md3-surface-container rounded-full overflow-hidden">
                      <div className={`h-full ${t.progress_percentage === 100 ? "bg-green-500" : "bg-blue-500"}`} style={{ width: `${t.progress_percentage || 0}%` }} />
                    </div>
                    <span className="text-xs font-medium">{t.progress_percentage || 0}%</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-xs">
                  {showCompletedDate
                    ? (t.updated_at ? new Date(t.updated_at).toLocaleDateString() : "-")
                    : (
                      <span className={isOverdue(t.due_date, t.status) ? "text-red-600 font-semibold" : ""}>
                        {t.due_date || "-"}
                        {isOverdue(t.due_date, t.status) && <Badge className="ml-1 bg-red-100 text-red-700 text-[10px] py-0">Overdue</Badge>}
                      </span>
                    )
                  }
                </td>
                <td className="px-3 py-2 text-xs text-md3-on-surface-variant">
                  {t.last_log_date || "-"}
                  {t.work_log_count > 0 && <span className="ml-1 text-[10px]">· {t.work_log_count} log{t.work_log_count > 1 ? "s" : ""}</span>}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-col gap-1">
                    {statusBadge(t.status)}
                    {workflowBadge(t)}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1">
                    {t.role === "producer" && (t.workflow_state === "in_progress" || t.workflow_state === "rework") && t.progress_percentage === 100 && (
                      <Button size="sm" variant="outline" className="h-7 border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => handleSendForCheck(t.id)} data-testid={`my-task-sendcheck-${t.id}`}>
                        <Send className="h-3.5 w-3.5 mr-1" />
                        Send for Check
                      </Button>
                    )}
                    {t.workflow_state === "rework" && (t.rework_cycles || []).length > 0 && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-red-700 hover:bg-red-50" onClick={() => setHistoryTask(t)} data-testid={`my-task-history-${t.id}`}>
                        <History className="h-3.5 w-3.5 mr-1" />
                        Comments
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(t)} data-testid={`my-task-edit-${t.id}`}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-md3-display-small font-semibold text-md3-on-surface">My Workspace</h1>
            <p className="text-md3-on-surface-variant text-sm">Your assigned tasks and performance overview.</p>
          </div>
          <Button
            onClick={handlePullWorkLogs}
            disabled={pullLoading}
            variant="outline"
            className="border-md3-outline hover:bg-md3-surface-container-high rounded-md3-full whitespace-nowrap"
            data-testid="my-workspace-pull-work-logs-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            {pullLoading ? "Pulling..." : "Pull Work Logs to Timesheet"}
          </Button>
        </div>

        <div className="space-y-6">
          {/* My Tasks Card */}
          <Card className="shadow-md3-1 rounded-md3-lg border-0 bg-md3-surface-container">
            <CardHeader className="pb-3">
              <CardTitle className="text-md3-title-large flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-md3-primary" />
                My Tasks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <TabsList>
                    <TabsTrigger value="active" data-testid="my-tasks-active-tab">
                      Active {activeTasks.length > 0 && <Badge className="ml-2 bg-blue-100 text-blue-700">{activeTasks.length}</Badge>}
                    </TabsTrigger>
                    <TabsTrigger value="completed" data-testid="my-tasks-completed-tab">
                      Completed {completedTasks.length > 0 && <Badge className="ml-2 bg-green-100 text-green-700">{completedTasks.length}</Badge>}
                    </TabsTrigger>
                  </TabsList>
                  {activeTab === "completed" && (
                    <Select value={completedPeriod} onValueChange={setCompletedPeriod}>
                      <SelectTrigger className="w-44 bg-white" data-testid="my-tasks-completed-period">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPLETED_PERIOD_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                {tasksLoading ? (
                  <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-md3-primary" /></div>
                ) : (
                  <>
                    <TabsContent value="active" className="m-0">{renderTaskTable(activeTasks, false)}</TabsContent>
                    <TabsContent value="completed" className="m-0">{renderTaskTable(completedTasks, true)}</TabsContent>
                  </>
                )}
              </Tabs>
            </CardContent>
          </Card>

          {/* My Follow-Ups (Payment / other follow-ups scheduled) */}
          <FollowUpsCard user={user} />

          {/* Tasks To Check (Checking Queue inside My Workspace) */}
          <CheckingQueueCard />

          {/* My Performance Card */}
          <Card className="shadow-md3-1 rounded-md3-lg border-0 bg-md3-surface-container">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-md3-title-large flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-md3-primary" />
                My Performance
              </CardTitle>
              <Select value={perfPeriod} onValueChange={setPerfPeriod}>
                <SelectTrigger className="w-44 bg-white" data-testid="my-perf-period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              {perfLoading || !performance ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-md3-primary" /></div>
              ) : (
                <div className="space-y-4">
                  <p className="text-xs text-md3-on-surface-variant">Period: <strong>{performance.period_label}</strong></p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="rounded-md3-md border border-md3-outline-variant bg-white p-4">
                      <div className="flex items-center gap-2 text-md3-on-surface-variant text-xs">
                        <CheckCircle2 className="h-4 w-4 text-green-600" /> Approved Hours
                      </div>
                      <div className="text-2xl font-bold text-green-700 mt-1" data-testid="perf-approved-hours">{performance.hours.approved}</div>
                      <p className="text-[11px] text-md3-on-surface-variant">From timesheets approved</p>
                    </div>
                    <div className="rounded-md3-md border border-md3-outline-variant bg-white p-4">
                      <div className="flex items-center gap-2 text-md3-on-surface-variant text-xs">
                        <Clock className="h-4 w-4 text-blue-600" /> Actual Hours
                      </div>
                      <div className="text-2xl font-bold text-blue-700 mt-1" data-testid="perf-actual-hours">{performance.hours.actual_total}</div>
                      <p className="text-[11px] text-md3-on-surface-variant">Approved + pending</p>
                    </div>
                    <div className="rounded-md3-md border border-md3-outline-variant bg-white p-4">
                      <div className="flex items-center gap-2 text-md3-on-surface-variant text-xs">
                        <AlertTriangle className="h-4 w-4 text-red-600" /> Errors
                      </div>
                      <div className="text-2xl font-bold text-red-700 mt-1" data-testid="perf-error-score">{performance.errors.score}</div>
                      <p className="text-[11px] text-md3-on-surface-variant">{performance.errors.count} error(s) · score</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Log Work Dialog */}
      <Dialog open={!!editTask} onOpenChange={(o) => !o && setEditTask(null)}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-md3-primary" />
              Log Work
            </DialogTitle>
          </DialogHeader>
          {editTask && (
            <div className="space-y-4 text-sm">
              {/* Task summary */}
              <div className="bg-md3-surface-container rounded-md3-sm p-3 space-y-1">
                <p className="font-semibold">{editTask.description}</p>
                <p className="text-xs text-md3-on-surface-variant">
                  {editTask.project_number} · {editTask.activity_type?.replace(/_/g, " ")} · Allocated {fh(editTask.allocated_hours)} hrs
                </p>
                <div className="flex items-center gap-3 text-xs">
                  <span><strong>{fh(editTask.actual_hours)}</strong> / {fh(editTask.allocated_hours)} hrs logged</span>
                  <span>Progress: <strong>{editTask.progress_percentage || 0}%</strong></span>
                  {statusBadge(editTask.status)}
                </div>
              </div>

              {/* Add new log */}
              <div className="border border-md3-outline-variant rounded-md3-md p-3 space-y-3 bg-blue-50/30">
                <p className="text-xs font-semibold uppercase tracking-wide text-md3-on-surface-variant">Add Work Log</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Date *</Label>
                    <Input type="date" value={logForm.date} onChange={(e) => setLogForm({ ...logForm, date: e.target.value })} className="bg-white mt-1" data-testid="log-date-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Hours Worked *</Label>
                    <Input type="number" step="0.25" min="0" placeholder="e.g. 3.5" value={logForm.hours} onChange={(e) => setLogForm({ ...logForm, hours: e.target.value })} className="bg-white mt-1" data-testid="log-hours-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Status *</Label>
                    <Select value={logForm.status} onValueChange={(v) => setLogForm({ ...logForm, status: v, percentage: v === "completed" ? "" : logForm.percentage })}>
                      <SelectTrigger className="bg-white mt-1" data-testid="log-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="partial">Partial</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {logForm.status === "partial" && (
                    <div>
                      <Label className="text-xs">Cumulative % *</Label>
                      <Input type="number" min="1" max="99" placeholder="e.g. 50" value={logForm.percentage} onChange={(e) => setLogForm({ ...logForm, percentage: e.target.value })} className="bg-white mt-1" data-testid="log-percentage-input" />
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Note (optional)</Label>
                  <Input value={logForm.note} onChange={(e) => setLogForm({ ...logForm, note: e.target.value })} placeholder="What did you work on?" className="bg-white mt-1" />
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleAddLog} disabled={saving} className="bg-md3-primary text-white" data-testid="log-save-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Add Log
                  </Button>
                </div>
              </div>

              {/* History */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-md3-on-surface-variant mb-2">
                  History · {taskLogs.length} entr{taskLogs.length === 1 ? "y" : "ies"}
                </p>
                <div className="border border-md3-outline-variant rounded-md3-sm overflow-hidden bg-white">
                  {logsLoading ? (
                    <div className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                  ) : taskLogs.length === 0 ? (
                    <p className="text-center py-6 text-md3-on-surface-variant text-xs">No work logs yet.</p>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-md3-surface-container">
                        <tr className="text-left">
                          <th className="px-3 py-2">Date</th>
                          <th className="px-3 py-2 text-right">Hours</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2 text-right">%</th>
                          <th className="px-3 py-2">Note</th>
                          <th className="px-3 py-2">Logged by</th>
                          <th className="px-3 py-2 text-right"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {taskLogs.map((l) => {
                          const created = l.created_at ? new Date(l.created_at) : null;
                          const within24h = created ? (Date.now() - created.getTime() < 24 * 3600 * 1000) : false;
                          const canDelete = within24h;  // backend will also enforce
                          return (
                            <tr key={l.id} className="border-t border-md3-outline-variant">
                              <td className="px-3 py-2">{l.date}</td>
                              <td className="px-3 py-2 text-right font-medium">{l.hours}</td>
                              <td className="px-3 py-2">
                                <Badge className={l.status === "completed" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}>{l.status}</Badge>
                              </td>
                              <td className="px-3 py-2 text-right">{l.percentage}%</td>
                              <td className="px-3 py-2 text-md3-on-surface-variant max-w-xs truncate" title={l.note}>{l.note || "-"}</td>
                              <td className="px-3 py-2 text-md3-on-surface-variant">{l.user_name}</td>
                              <td className="px-3 py-2 text-right">
                                {canDelete && (
                                  <button onClick={() => handleDeleteLog(l.id)} className="text-red-600 hover:underline text-xs" data-testid={`log-delete-${l.id}`}>Delete</button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTask(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rework History Dialog */}
      <Dialog open={!!historyTask} onOpenChange={(o) => !o && setHistoryTask(null)}>
        <DialogContent className="bg-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-orange-600" />
              Checker Comments — {historyTask?.description}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {(historyTask?.rework_cycles || []).map((c, i) => (
              <div key={i} className="border border-orange-200 bg-orange-50 rounded p-3 text-sm">
                <div className="flex justify-between items-center mb-1">
                  <Badge className="bg-orange-100 text-orange-700">Cycle {c.cycle}</Badge>
                  <span className="text-xs text-md3-on-surface-variant">
                    {c.sent_back_by_name} · {c.sent_back_at ? new Date(c.sent_back_at).toLocaleString() : ""}
                  </span>
                </div>
                <div className="text-md3-on-surface whitespace-pre-wrap">{c.comments}</div>
              </div>
            ))}
            {(historyTask?.rework_cycles || []).length === 0 && (
              <div className="text-sm text-md3-on-surface-variant text-center py-4">No comments yet.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryTask(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
